<template>
  <div class="VersionSetting">
    <!-- Published Version Section -->
    <div v-if="published" class="mb-6">
      <div class="text-overline grey--text mb-1">Published Version</div>
      <v-card
        outlined
        :class="{
          'primary--text v-card--selected': isSelected(published),
          'elevation-2': isSelected(published)
        }"
        class="transition-swing"
        @click="selectVersion(published)"
      >
        <v-card-text class="pa-3">
          <div v-if="!(edit && edit.type === 'published')">
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1 mr-2">
                <div class="d-flex align-center mb-1">
                  <span class="text-subtitle-1 font-weight-medium text-truncate">
                    {{ published.name || 'Unnamed Version' }}
                  </span>
                  <v-chip
                    x-small
                    color="success"
                    class="ml-2 font-weight-bold"
                    label
                  >
                    PUBLISHED
                  </v-chip>
                </div>
                
                <div class="text-caption grey--text mb-1">
                  <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatDate(published.createdAt) }}
                </div>
                <div class="text-caption grey--text">
                  <v-icon x-small class="mr-1">mdi-account-outline</v-icon>
                  {{ getCreatedBy(published.createdBy) }}
                </div>

                <div v-if="published.description" class="text-body-2 mt-2 grey--text text--darken-1">
                  {{ published.description }}
                </div>
              </div>

              <div v-if="!selectVersionLoading">
                <v-btn
                  icon
                  small
                  @click.stop="editVersion({ version: published, type: 'published' })"
                  color="grey"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <v-progress-circular
                v-else
                indeterminate
                color="primary"
                size="20"
                width="2"
              ></v-progress-circular>
            </div>
          </div>

          <!-- Edit Form -->
          <div v-else class="pa-1">
            <v-text-field
              label="Name"
              outlined
              dense
              v-model="edit.name"
              class="mb-2"
              hide-details="auto"
            ></v-text-field>
            <v-textarea
              label="Description"
              outlined
              dense
              rows="2"
              v-model="edit.description"
              class="mb-2"
              hide-details="auto"
            ></v-textarea>
            <div class="d-flex justify-end">
              <v-btn text small @click="edit = null" class="mr-2">Cancel</v-btn>
              <v-btn color="primary" small depressed @click="saveEdit()">Save</v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-divider class="mb-4"></v-divider>

    <!-- All Versions Section -->
    <div class="mb-4">
      <div class="text-overline grey--text mb-2">Version History</div>
      <v-text-field
        label="Search versions"
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        v-model="search"
        @input="debounceInput"
        hide-details
        clearable
      ></v-text-field>
    </div>

    <div v-if="workflowVersionsLoading" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else class="VersionSetting__list">
      <v-card
        v-for="(item, index) in versions"
        :key="item.id"
        outlined
        class="mb-3 transition-swing"
        :class="{
          'primary--text v-card--selected': isSelected(item),
          'elevation-2': isSelected(item)
        }"
        :ripple="edit?.id !== item?.id"
        @click="selectVersion(item)"
      >
        <v-card-text class="pa-3">
          <div v-if="!(edit && edit.type === 'list' && edit.id === item.id)">
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1 mr-2" style="min-width: 0">
                <div class="d-flex align-center mb-1">
                  <span class="text-subtitle-2 font-weight-bold text-truncate">
                    {{ item.name || 'Unnamed Version' }}
                  </span>
                  <v-chip
                    v-if="isPublished(item)"
                    x-small
                    color="success"
                    class="ml-2"
                    label
                    outlined
                  >
                    PUBLISHED
                  </v-chip>
                </div>

                <div class="text-caption grey--text mb-1">
                  {{ formatDate(item.createdAt) }}
                </div>
                <div v-if="item?.createdBy?.email" class="text-caption grey--text text-truncate">
                  by {{ getCreatedBy(item.createdBy) }}
                </div>

                <div v-if="item.description" class="text-caption mt-2 grey--text text--darken-2 text-truncate">
                  {{ item.description }}
                </div>
              </div>

              <!-- Actions -->
              <div class="d-flex flex-column align-end">
                <div v-if="!selectVersionLoading" class="d-flex">
                  <template v-if="!overwriteVersion">
                    <v-menu bottom left offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          @click.stop
                        >
                          <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          v-if="!isPublished(item)"
                          @click="publishVersions(item)"
                          :disabled="deleteVersionLoading"
                        >
                          <v-list-item-icon class="mr-2">
                            <v-icon small>mdi-publish</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Publish</v-list-item-title>
                        </v-list-item>
                        
                        <v-list-item
                          @click="editVersion({ version: item, type: 'list' })"
                          :disabled="deleteVersionLoading"
                        >
                          <v-list-item-icon class="mr-2">
                            <v-icon small>mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Edit Details</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          v-if="!isPublished(item) && !isSelected(item)"
                          @click="handleDeleteVersion(item)"
                          :disabled="deleteVersionLoading"
                          class="error--text"
                        >
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="error">mdi-delete</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                  <v-btn
                    v-else
                    icon
                    small
                    @click.stop="handleSelectOverwrite(item)"
                    title="Overwrite Version"
                    color="primary"
                  >
                    <v-icon small>mdi-file-edit</v-icon>
                  </v-btn>
                </div>
                <v-progress-circular
                  v-else
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                ></v-progress-circular>
              </div>
            </div>

            <!-- Commits Toggle -->
            <div class="mt-2 pt-2 border-top d-flex justify-center" v-if="item.commits && item.commits.length > 0">
              <v-btn
                text
                x-small
                color="grey darken-1"
                @click.stop="showCommits(item)"
                class="px-2"
              >
                {{ commitsForVersion?.id === item?.id ? 'Hide Commits' : `${item.commits.length} Commits` }}
                <v-icon right x-small>
                  {{ commitsForVersion?.id === item?.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Edit Form -->
          <div v-else class="pa-1">
            <v-text-field
              label="Name"
              outlined
              dense
              v-model="edit.name"
              class="mb-2"
              hide-details="auto"
            ></v-text-field>
            <v-textarea
              label="Description"
              outlined
              dense
              rows="2"
              v-model="edit.description"
              class="mb-2"
              hide-details="auto"
            ></v-textarea>
            <div class="d-flex justify-end">
              <v-btn text small @click="edit = null" class="mr-2">Cancel</v-btn>
              <v-btn color="primary" small depressed @click="saveEdit(index)">Save</v-btn>
            </div>
          </div>

          <!-- Commits List -->
          <v-expand-transition>
            <div v-if="commitsForVersion && commitsForVersion?.id === item?.id" class="grey lighten-5 mt-2 rounded">
              <div
                v-for="commit in item.commits"
                :key="commit.id"
                class="pa-2 border-bottom d-flex justify-space-between align-center hover-bg"
                :class="{'primary--text font-weight-bold': isSelectedCommit(commit)}"
                style="cursor: pointer; border-bottom: 1px solid rgba(0,0,0,0.05);"
                @click.stop="selectCommit(item, commit)"
              >
                <div class="text-caption">
                  <div v-if="commit.message" class="text-truncate" style="max-width: 200px;">
                    {{ commit.message }}
                  </div>
                  <div class="grey--text" style="font-size: 10px;">
                    {{ formatDate(commit.createdAt) }}
                  </div>
                </div>
                <v-btn
                  icon
                  x-small
                  @click.stop="handleRestoreCommit(commit, item)"
                  title="Restore Commit"
                  :loading="restoreCommitLoading"
                >
                  <v-icon x-small>mdi-restore</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import * as moment from 'moment-timezone';
import debounce from 'lodash/debounce';
import { createNamespacedHelpers } from 'vuex';

const {
  mapGetters: workflowDetailsGetters,
  mapActions: workflowDetailsActions,
  mapMutations: workflowDetailsMutations,
} = createNamespacedHelpers('workflows/details');

export default {
  name: 'VersionSetting',
  components: {},
  props: {},
  computed: {
    ...workflowDetailsGetters({
      workflow: 'WORKFLOW',
      versions: 'WORKFLOW_VERSIONS',
      published: 'WORKFLOW_PUBLISHED_VERSION',
      selected: 'WORKFLOW_SELECTED_VERSION',
      selectedCommit: 'WORKFLOW_SELECTED_COMMIT',
      workflowVersionsLoading: 'WORKFLOW_VERSIONS_LOADING',
      publishVersionLoading: 'PUBLISH_WORKFLOW_VERSION_LOADING',
      selectVersionLoading: 'SELECT_WORKFLOW_VERSION_LOADING',
      updateVersionLoading: 'UPDATE_WORKFLOW_VERSION_LOADING',
      deleteVersionLoading: 'DELETE_WORKFLOW_VERSION_LOADING',
      overwriteVersion: 'SET_OPEN_VERSION_MODAL_FROM_OVERWRITE',
      restoreCommitLoading: 'RESTORE_COMMIT_LOADING',
    }),
  },
  data() {
    return {
      localTZ: 'UTC',
      search: '',
      edit: null,
      commitsForVersion: null,
    };
  },
  methods: {
    ...workflowDetailsActions([
      'publishWorkflowVersion',
      'overwriteWorkflow',
      'setWorkflowVersion',
      'updateWorkflowVersion',
      'getWorkflowVersions',
      'runWorkflow',
      'deleteWorkflowVersion',
      'restoreCommit',
    ]),
    ...workflowDetailsMutations({
      setPublished: 'SET_WORKFLOW_PUBLISHED_VERSION',
      setSelected: 'SET_WORKFLOW_SELECT_VERSION',
      setVersions: 'SET_WORKFLOW_VERSIONS',
      openVersionModalFromRun: 'SET_OPEN_VERSION_MODAL_FROM_RUN',
    }),
    isSelected(version) {
      return this.selected.id === version.id;
    },
    isSelectedCommit(commit) {
      return this.selectedCommit?.id === commit.id;
    },
    async handlerRunSelectedVersion(item, production = false) {
      await this.selectVersion(item);
      await this.runWorkflow({
        isProduction: production,
        isNew: this.isNew,
        isSelectedVersionRun: true,
      });
      if (this.openVersionModalFromRun) {
        this.openVersionModalFromRun(false);
      }
    },
    handleSelectOverwrite(versionItem) {
      this.overwriteWorkflow({ versionItem });
    },
    async handleDeleteVersion(versionItem) {
      await this.deleteWorkflowVersion({ versionItem });
      await this.getWorkflowVersions({
        workflow_id: this.workflow.id,
        search: this.search,
      });
    },
    handleRestoreCommit(commit, version) {
      this.restoreCommit({
        workflow_id: this.workflow.id,
        version_id: version.id,
        commit_id: commit.id,
      });
    },
    isPublished(version) {
      return this.published.id === version.id;
    },
    showCommits(version) {
      if (this.commitsForVersion?.id === version?.id) {
        this.commitsForVersion = null;
        return;
      }
      this.commitsForVersion = version;
    },
    formatDate(date) {
      return moment(date).tz(this.localTZ).format('YYYY-MM-DD, HH:mm:SS');
    },
    setLocalTimezone() {
      this.localTZ = moment.tz.guess();
    },
    getCreatedBy(createdBy) {
      if (!createdBy) {
        return ``;
      }
      if (!createdBy.firstName && !createdBy.lastName) {
        return createdBy.email;
      }
      return `${createdBy.firstName} ${createdBy.lastName} (${createdBy.email})`;
    },
    editVersion(edit) {
      this.edit = {
        ...edit.version,
        type: edit.type,
      };
    },
    async saveEdit(index) {
      if (!this.edit?.id) {
        return;
      }
      const version = await this.updateWorkflowVersion({
        version_id: this.edit.id,
        workflow_id: this.workflow.id,
        name: this.edit.name,
        description: this.edit.description,
      });

      if (
        this.edit.type === 'published' ||
        this.edit.id === this.published.id
      ) {
        this.setPublished({
          ...this.selected,
          name: version.name,
          description: version.description,
        });
      }

      if (this.edit.type === 'list') {
        if (typeof index !== 'undefined') {
          const versions = [...this.versions];
          const prev = versions[index];
          versions[index] = {
            ...prev,
            name: version.name,
            description: version.description,
          };
          this.setVersions([...versions]);
        }
      } else {
        const fIndex = this.versions.findIndex(
          (item) => item.id === version.id
        );
        if (fIndex > -1) {
          const versions = [...this.versions];
          versions[fIndex] = version;
          this.setVersions([...versions]);
        }
      }

      if (this.edit.type === 'selected' || this.edit.id === this.selected.id) {
        this.setSelected({
          ...this.selected,
          name: version.name,
          description: version.description,
        });
      }

      this.edit = null;
    },
    selectVersion(version) {
      if (this.selected?.id === version?.id) {
        return;
      }
      this.setWorkflowVersion({
        workflow_id: this.workflow.id,
        version_id: version.id,
      });
    },
    selectCommit(version, commit) {
      if (this.selectedCommit?.id === commit.id) {
        return;
      }
      this.setWorkflowVersion({
        workflow_id: this.workflow.id,
        version_id: version.id,
        commit_id: commit.id,
      });
    },
    publishVersions(version) {
      this.publishWorkflowVersion({
        workflow_id: this.workflow.id,
        version_id: version.id,
      });
    },
    debounceInput: debounce(function (e) {
      this.search = e;
      this.getWorkflowVersions({
        workflow_id: this.workflow.id,
        search: this.search,
      });
    }, 700),
  },
  destroyed() {
    this.setVersions([]);
  },
  mounted() {
    if (this.workflow?.id) {
      this.getWorkflowVersions({
        workflow_id: this.workflow.id,
        search: '',
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import './version-setting.scss';
</style>
